import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Client } from "@/types/client";
import { useCompanies } from "@/hooks/useCompanies";
import { CompanySelector } from "./ClientDialog/CompanySelector";
import { ClientInfoInputs } from "./ClientDialog/ClientInfoInputs";
import { PassportInfoTab } from "./ClientDialog/PassportInfoTab";
import { AdditionalInfoTab } from "./ClientDialog/AdditionalInfoTab";
import { AttachmentsTab } from "./ClientDialog/AttachmentsTab";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, X, User, FileText, Building2, Paperclip } from "lucide-react";

interface ClientEditDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type FormData = Omit<Client, "created_at" | "updated_at" | "client_assignments">;

const getInitialFormData = (client?: Client): FormData => ({
  id: client?.id || "",
  company_id: client?.company_id || undefined,
  company: client?.company || "",
  name_ar: client?.name_ar || "",
  name_en: client?.name_en || "",
  mobile: client?.mobile || "",
  additional_info: client?.additional_info || "",
  passport_info: client?.passport_info || null,
  attachments: client?.attachments || [],
  status: client?.status || "waiting",
  client_type: client?.client_type || "direct",
  created_by: client?.created_by || "",
  creator: client?.creator || undefined,
  companies: client?.companies || undefined,
  employees: client?.employees || undefined
});

export const ClientEditDialog = ({ client, open, onOpenChange, onSuccess }: ClientEditDialogProps) => {
  const [formData, setFormData] = useState<FormData>(getInitialFormData(client));
  const [activeTab, setActiveTab] = useState("basic");
  const { data: companies = [] } = useCompanies();
  const updateClientMutation = useUpdateClient();

  useEffect(() => {
    if (client) {
      setFormData(getInitialFormData(client));
    }
  }, [client]);

  const handleFormChange = (data: Partial<FormData>) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };

  const handleCompanyAdded = (companyId: string, companyName: string) => {
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: companyName
    }));
  };

  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company: selectedCompany?.name_ar || ""
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name_ar.trim()) {
      toast.error("الرجاء إدخال الاسم باللغة العربية");
      setActiveTab("basic");
      return;
    }

    if (!formData.id) {
      toast.error("معرف العميل غير موجود");
      return;
    }

    try {
      await updateClientMutation.mutateAsync(formData);
      toast.success("تم تحديث بيانات العميل بنجاح");
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error updating client:", error);
      toast.error(error.message || "حدث خطأ أثناء تحديث العميل");
    }
  };

  const tabsConfig = [
    {
      key: "basic",
      label: "المعلومات الأساسية",
      icon: User,
      component: (
        <div className="space-y-6">
          <CompanySelector 
            companyId={formData.company_id || ""} 
            onCompanyChange={handleCompanyChange} 
            onCompanyAdded={handleCompanyAdded} 
            companies={companies} 
          />
          <ClientInfoInputs formData={formData} onFormChange={handleFormChange} />
        </div>
      )
    },
    {
      key: "additional",
      label: "معلومات إضافية",
      icon: FileText,
      component: <AdditionalInfoTab formData={formData} onFormChange={handleFormChange} />
    },
    {
      key: "passport",
      label: "معلومات الجواز",
      icon: Building2,
      component: <PassportInfoTab formData={formData} onFormChange={handleFormChange} />
    },
    {
      key: "attachments",
      label: "المرفقات",
      icon: Paperclip,
      component: <AttachmentsTab formData={formData} onFormChange={handleFormChange} />
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-bold">
            تعديل بيانات العميل: {client?.name_ar}
          </DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              {tabsConfig.map(tab => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger key={tab.key} value={tab.key} className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {tabsConfig.map(tab => (
              <TabsContent key={tab.key} value={tab.key} className="mt-6">
                <div className="min-h-[300px]">
                  {tab.component}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              disabled={updateClientMutation.isPending || !formData.name_ar.trim()}
              className="flex items-center gap-2"
            >
              {updateClientMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  جاري التحديث...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  حفظ التغييرات
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};